import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import DoctorShell from "@/components/doctor/DoctorShell";
import PatientList from "@/components/doctor/PatientList";
import PatientDetailView from "@/components/doctor/PatientDetailView";
import DoctorAlertsPanel from "@/components/doctor/DoctorAlertsPanel";
import ClinicalTemplatesPanel from "@/components/doctor/ClinicalTemplatesPanel";
import MetabolicSubphenotypePanel from "@/components/doctor/MetabolicSubphenotypePanel";
import DoctorComplianceAuditPanel from "@/components/doctor/DoctorComplianceAuditPanel";
import NephroCalculatorPanel from "@/components/doctor/NephroCalculatorPanel";
import DoctorSoapRecorder from "@/components/doctor/DoctorSoapRecorder";
import CreatePatientWizard from "@/components/doctor/CreatePatientWizard";
import DoctorMobileBottomNav from "@/components/doctor/DoctorMobileBottomNav";
import SharedPatientsWidget from "@/components/doctor/SharedPatientsWidget";  // iter570 — Phase 7c
import SEO from "@/components/SEO";
import { Plus, Users, Loader2, ChevronLeft, AlertCircle, UserPlus, Pause } from "lucide-react";

// iter351.F — clinical patient-facing modules surfaced inside doctor's MD tab.
// Lazy-loaded to keep doctor portal initial bundle lean.
const ClinicalAnalysis = React.lazy(() => import("@/components/ClinicalAnalysis"));
const PhenotypeClassifier = React.lazy(() => import("@/components/PhenotypeClassifier"));
const PathwayCoverageCard = React.lazy(() => import("@/components/PathwayCoverageCard"));
const MindBodyRecommender = React.lazy(() => import("@/components/MindBodyRecommender"));
const EpigeneticMapPanel = React.lazy(() => import("@/components/EpigeneticMapPanel"));

const DOCTOR_ROLES = ["admin", "clinician"];

/**
 * Doctor Dashboard — Phase A core workflow.
 * Layout: DoctorShell sidebar + Patient list column + detail view.
 * (#7) Alerts/Templates панели се отварят чрез doctor-nav event от sidebar.
 * (#8) AI chat преместен в PatientDetailView таб „Попитай AI" (преди Notes).
 */
export default function DoctorDashboardPage() {
  const { user, API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);

  // Right column panel: alerts | templates | subphenotype | nephro | soap
  const [rightPanel, setRightPanel] = useState("alerts");

  // Mobile-only: which bottom-tab section is active.
  // Auto-jumps to "detail" when a patient is selected from the list (better UX).
  const [mobileSection, setMobileSection] = useState("patients");

  // Tools accordion (mobile only) — which tool is expanded inside Tools tab.
  const [mobileToolOpen, setMobileToolOpen] = useState("alerts");

  // iter427 — Dashboard summary tiles за empty state на desktop.
  const [summary, setSummary] = useState(null);
  // iter427 — клик филтър от summary tiles → филтрира /doctor/patients
  const [activeFilter, setActiveFilter] = useState(null); // null | "alerts_high" | "inactive_5d" | "new_this_week"
  const fetchSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
      const { data } = await axios.get(`${API}/doctor/dashboard-summary`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setSummary(data);
    } catch {
      setSummary(null);
    }
  }, [API]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Listen for sidebar nav clicks (#alerts / #templates).
  useEffect(() => {
    const handler = (e) => {
      const key = e.detail?.key;
      if (key === "alerts" || key === "templates" || key === "subphenotype" || key === "nephro" || key === "soap") setRightPanel(key);
    };
    window.addEventListener("doctor-nav", handler);
    return () => window.removeEventListener("doctor-nav", handler);
  }, []);

  // Access guard
  useEffect(() => {
    if (!user) return;
    if (!DOCTOR_ROLES.includes(user.role)) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
      const qs = new URLSearchParams();
      if (search) qs.set("q", search);
      if (activeFilter) qs.set("filter", activeFilter);
      const { data } = await axios.get(
        `${API}/doctor/patients${qs.toString() ? `?${qs.toString()}` : ""}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setPatients(data.patients || []);
      // При филтър изчистваме selection за да покажем целия списък
      if (activeFilter) {
        setSelectedId(null);
      } else if (!selectedId && data.patients?.length) {
        setSelectedId(data.patients[0].id);
      }
    } catch {
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [API, search, selectedId, activeFilter]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  const handleCreated = (newPatient) => {
    setWizardOpen(false);
    fetchPatients().then(() => setSelectedId(newPatient.id));
  };

  const selectedPatient = patients.find(p => p.id === selectedId) || null;

  // Mobile-only: when user picks a patient from the list, auto-switch to Преглед tab.
  const handleMobilePatientSelect = (id) => {
    setSelectedId(id);
    setMobileSection("detail");
  };

  // Tools accordion item renderer for mobile Инструменти tab
  const ToolItem = ({ id, label, color, children }) => {
    const open = mobileToolOpen === id;
    return (
      <div
        className={`rounded-2xl border overflow-hidden ${
          isDark ? "border-white/5 bg-[hsl(222,47%,9%)]" : "border-gray-200 bg-white"
        }`}
      >
        <button
          type="button"
          onClick={() => setMobileToolOpen(open ? null : id)}
          aria-expanded={open}
          data-testid={`doctor-mobile-tool-${id}-toggle`}
          className="w-full px-4 py-3 flex items-center justify-between text-left"
        >
          <span className="flex items-center gap-2">
            <span
              className="w-1.5 h-6 rounded-full"
              style={{ background: `linear-gradient(180deg, ${color}, ${color}cc)` }}
              aria-hidden="true"
            />
            <span className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{label}</span>
          </span>
          <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            {open ? "−" : "+"}
          </span>
        </button>
        {open && <div className="px-3 pb-3" data-testid={`doctor-mobile-tool-${id}-body`}>{children}</div>}
      </div>
    );
  };

  return (
    <>
      <SEO
        title={bg ? "Доктор портал — Nutramilo" : "Doctor Portal — Nutramilo"}
        description={bg ? "Управление на пациенти, AI анализи и клинични препоръки."
          : "Patient management, AI analysis and clinical recommendations."}
        path="/doctor"
      />
      <DoctorShell>
        <div className={`flex flex-col h-full ${isDark ? "bg-[hsl(222,47%,6%)]" : "bg-[#F8FAFC]"}`}
          data-testid="doctor-dashboard-page">
          {/* Top header — compact on mobile, expanded on desktop */}
          <header className={`flex items-center justify-between gap-3 px-3 sm:px-6 py-3 sm:py-4 border-b
            ${isDark ? "border-white/5 bg-[hsl(222,47%,8%)]" : "border-gray-200 bg-white"}`}>
            <div className="min-w-0 flex-1">
              <h1 className={`text-base sm:text-2xl font-bold truncate ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? "Преглед на пациенти" : "Patient Overview"}
              </h1>
              <p className={`hidden sm:block text-xs sm:text-sm mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg ? "Управлявайте и преглеждайте здравните данни и прогреса на пациентите"
                  : "View and manage patient health data and progress"}
              </p>
              <p className={`sm:hidden text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {patients.length} {bg ? "пациента" : "patients"}
              </p>
            </div>
            <button
              onClick={() => setWizardOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 sm:px-4 h-9 sm:h-10 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600
                text-white text-xs sm:text-sm font-semibold hover:opacity-95 transition-opacity shadow-sm shadow-violet-900/20 shrink-0"
              data-testid="create-patient-button"
              aria-label={bg ? "Създай пациент" : "Create Patient"}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{bg ? "Създай пациент" : "Create Patient"}</span>
              <span className="sm:hidden">{bg ? "Нов" : "New"}</span>
            </button>
          </header>

          {/* iter427 — Compact summary strip (always visible on desktop, дава бърз
              контекст дори когато е избран пациент). Скрит на мобилно (бот навигация).
              iter427.2 — tiles стават кликаеми филтри (alerts/inactive/new) с активно
              състояние и clear-бутон. */}
          {summary && (
            <div className={`hidden lg:flex items-center gap-2 px-3 sm:px-6 py-2 border-b text-[11px] ${
              isDark ? "border-white/5 bg-[hsl(222,47%,7%)]" : "border-gray-200 bg-gray-50/60"
            }`} data-testid="doctor-summary-strip">
              {[
                { key: "total", filter: null, icon: Users, value: summary.total_patients, label_bg: "Общо", label_en: "Total", color: isDark ? "text-sky-300" : "text-sky-700" },
                { key: "new", filter: "new_this_week", icon: UserPlus, value: summary.new_this_week, label_bg: "Нови (7д)", label_en: "New (7d)", color: isDark ? "text-emerald-300" : "text-emerald-700" },
                { key: "alerts", filter: "alerts_high", icon: AlertCircle, value: summary.alerts_high, label_bg: "Алерти", label_en: "Alerts", color: isDark ? "text-rose-300" : "text-rose-700" },
                { key: "inactive", filter: "inactive_5d", icon: Pause, value: summary.inactive_5d, label_bg: "Неактивни 5+д", label_en: "Inactive 5+d", color: isDark ? "text-amber-300" : "text-amber-700" },
              ].map((t) => {
                const Icon = t.icon;
                const isActive = activeFilter === t.filter && t.filter != null;
                const clickable = t.filter !== null;
                const baseCls = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold transition-colors";
                const activeCls = isDark
                  ? "bg-violet-500/30 text-violet-200 border border-violet-500/40 ring-1 ring-violet-500/40"
                  : "bg-violet-600 text-white border border-violet-600 ring-1 ring-violet-300";
                const idleCls = isDark
                  ? "bg-white/5 hover:bg-white/10"
                  : "bg-white border border-gray-200 hover:border-gray-300";
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={clickable
                      ? () => setActiveFilter((prev) => prev === t.filter ? null : t.filter)
                      : undefined}
                    disabled={!clickable}
                    className={`${baseCls} ${isActive ? activeCls : idleCls} ${clickable ? "cursor-pointer" : "cursor-default"}`}
                    data-testid={`doctor-strip-tile-${t.key}`}
                    title={clickable ? (bg ? "Кликнете за филтър на патиент списъка" : "Click to filter patient list") : undefined}
                    aria-pressed={isActive}
                  >
                    <Icon className={`w-3 h-3 ${isActive ? "" : t.color}`} />
                    <span className={isActive ? "" : (isDark ? "text-gray-400" : "text-gray-600")}>{bg ? t.label_bg : t.label_en}:</span>
                    <span className={isActive ? "" : (isDark ? "text-white" : "text-[#1A1A2E]")}>{t.value}</span>
                  </button>
                );
              })}
              {activeFilter && (
                <button onClick={() => setActiveFilter(null)}
                  className={`text-[11px] px-2.5 py-1 rounded-full font-bold inline-flex items-center gap-1 ${
                    isDark ? "text-rose-300 hover:bg-rose-500/15" : "text-rose-700 hover:bg-rose-50"
                  }`}
                  data-testid="doctor-clear-filter-btn"
                  title={bg ? "Изчисти активния филтър" : "Clear active filter"}>
                  ✕ {bg ? "Изчисти" : "Clear"}
                </button>
              )}
              {!activeFilter && selectedPatient && (
                <button onClick={() => setSelectedId(null)}
                  className={`ml-auto text-[11px] px-2.5 py-1 rounded-full font-bold ${
                    isDark ? "text-violet-300 hover:bg-violet-500/15" : "text-violet-700 hover:bg-violet-50"
                  }`}
                  data-testid="doctor-clear-selection-btn"
                  title={bg ? "Покажи преглед на портфолиото" : "Show portfolio overview"}>
                  {bg ? "Преглед" : "Overview"}
                </button>
              )}
            </div>
          )}

          {/* === DESKTOP: 2-column layout (≥lg) === */}
          <div className="hidden lg:grid flex-1 overflow-hidden grid-cols-1 lg:grid-cols-[300px,1fr] gap-0">
            {/* Patient list */}
            <aside className={`border-r overflow-y-auto
              ${isDark ? "border-white/5 bg-[hsl(222,47%,7%)]" : "border-gray-200 bg-white"}`}>
              {/* iter570 (Phase 7c) — Споделени пациенти (винаги видим, top of sidebar) */}
              <div className={`p-3 border-b ${isDark ? "border-white/5" : "border-gray-200"}`}>
                <SharedPatientsWidget />
              </div>
              <PatientList
                patients={patients}
                loading={loading}
                selectedId={selectedId}
                onSelect={setSelectedId}
                search={search}
                onSearch={setSearch}
              />
            </aside>

            {/* Main detail */}
            <main className="overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                </div>
              ) : selectedPatient ? (
                <PatientDetailView patient={selectedPatient} onRefresh={fetchPatients} />
              ) : (
                <div className={`p-4 sm:p-6 ${isDark ? "text-gray-300" : "text-gray-700"}`} data-testid="doctor-empty-state">
                  {/* iter427 — empty state със summary tiles + CTA */}
                  <div className={`flex flex-col items-center justify-center text-center px-6 py-8 mb-6 rounded-2xl border ${isDark ? "border-white/5 bg-white/[0.02]" : "border-gray-200 bg-white"}`}>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white mb-3">
                      <Users className="w-7 h-7" />
                    </div>
                    <h2 className={`text-base sm:text-lg font-bold mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {bg ? "Изберете пациент или създайте нов" : "Select a patient or create a new one"}
                    </h2>
                    <p className={`text-xs sm:text-sm max-w-md ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      {bg
                        ? "Кратък преглед на портфолиото Ви по-долу. Кликнете върху плочка за бърз филтър."
                        : "Quick portfolio overview below. Click a tile for instant filtering."}
                    </p>
                    <button
                      onClick={() => setWizardOpen(true)}
                      className="mt-3 inline-flex items-center gap-1.5 px-3.5 h-9 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-semibold shadow-sm shadow-violet-900/20"
                      data-testid="doctor-empty-create-cta"
                    >
                      <Plus className="w-4 h-4" />
                      {bg ? "Създай пациент" : "Create patient"}
                    </button>
                  </div>

                  {/* Summary tiles: общо · нови · алерти · неактивни (кликаеми филтри) */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="doctor-summary-tiles">
                    {[
                      { key: "total", filter: null, icon: Users, value: summary?.total_patients ?? patients.length,
                        label_bg: "Общо пациенти", label_en: "Total patients",
                        color: isDark ? "from-sky-600/40 to-cyan-600/40" : "from-sky-50 to-cyan-50",
                        icon_color: isDark ? "text-sky-300" : "text-sky-600" },
                      { key: "new", filter: "new_this_week", icon: UserPlus, value: summary?.new_this_week ?? 0,
                        label_bg: "Нови (7 дни)", label_en: "New (7d)",
                        color: isDark ? "from-emerald-600/40 to-teal-600/40" : "from-emerald-50 to-teal-50",
                        icon_color: isDark ? "text-emerald-300" : "text-emerald-600" },
                      { key: "alerts", filter: "alerts_high", icon: AlertCircle, value: summary?.alerts_high ?? 0,
                        label_bg: "Високи алерти", label_en: "High alerts",
                        color: isDark ? "from-rose-600/40 to-pink-600/40" : "from-rose-50 to-pink-50",
                        icon_color: isDark ? "text-rose-300" : "text-rose-600" },
                      { key: "inactive", filter: "inactive_5d", icon: Pause, value: summary?.inactive_5d ?? 0,
                        label_bg: "Неактивни 5+ дни", label_en: "Inactive 5+d",
                        color: isDark ? "from-amber-600/40 to-orange-600/40" : "from-amber-50 to-orange-50",
                        icon_color: isDark ? "text-amber-300" : "text-amber-600" },
                    ].map((t) => {
                      const Icon = t.icon;
                      const isActive = activeFilter === t.filter && t.filter != null;
                      const clickable = t.filter !== null;
                      return (
                        <button key={t.key}
                          type="button"
                          onClick={clickable ? () => setActiveFilter(t.filter) : undefined}
                          disabled={!clickable}
                          className={`text-left p-3.5 rounded-2xl border bg-gradient-to-br ${t.color} ${
                            isActive
                              ? (isDark ? "border-violet-500/60 ring-2 ring-violet-500/40" : "border-violet-500 ring-2 ring-violet-300")
                              : (isDark ? "border-white/5" : "border-gray-200")
                          } ${clickable ? "hover:scale-[1.02] transition-transform cursor-pointer" : "cursor-default"}`}
                          data-testid={`doctor-summary-tile-${t.key}`}
                          aria-pressed={isActive}>
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className={`w-4 h-4 ${t.icon_color}`} />
                            <span className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {bg ? t.label_bg : t.label_en}
                            </span>
                          </div>
                          <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                            {t.value}
                          </div>
                          {clickable && (
                            <div className={`text-[10px] mt-1 ${isDark ? "text-violet-200/70" : "text-violet-700/70"}`}>
                              {isActive ? (bg ? "✓ Активен филтър" : "✓ Active filter") : (bg ? "Кликни за филтър →" : "Click to filter →")}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* iter570 (Phase 7c) — Споделени с мен пациенти (magic-link approved). Дублирано в empty state за лесна видимост */}
                  {/* GDPR / EU AI Act compliance audit (clinic-tier driver) */}
                  <DoctorComplianceAuditPanel />
                </div>
              )}
            </main>
          </div>

          {/* === MOBILE: 4-tab section layout (<lg) === */}
          <div className="lg:hidden flex-1 overflow-y-auto pb-20" data-testid="doctor-mobile-layout">
            {mobileSection === "patients" && (
              <div className="p-3" data-testid="doctor-mobile-section-patients">
                <PatientList
                  patients={patients}
                  loading={loading}
                  selectedId={selectedId}
                  onSelect={handleMobilePatientSelect}
                  search={search}
                  onSearch={setSearch}
                />
              </div>
            )}

            {mobileSection === "detail" && (
              <div data-testid="doctor-mobile-section-detail">
                <div className={`flex items-center gap-2 px-3 py-2 sticky top-0 z-10 backdrop-blur-md border-b ${
                  isDark ? "bg-[hsl(222,47%,8%)]/85 border-white/5" : "bg-white/85 border-gray-200"
                }`}>
                  <button
                    type="button"
                    onClick={() => setMobileSection("patients")}
                    className={`p-1.5 rounded-lg ${isDark ? "hover:bg-white/5 text-gray-300" : "hover:bg-gray-100 text-gray-600"}`}
                    data-testid="doctor-mobile-back-to-patients"
                    aria-label={bg ? "Назад към списъка" : "Back to list"}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className={`text-sm font-semibold truncate ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                    {selectedPatient?.name || (bg ? "Преглед на пациент" : "Patient Review")}
                  </span>
                </div>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                  </div>
                ) : selectedPatient ? (
                  <PatientDetailView patient={selectedPatient} onRefresh={fetchPatients} />
                ) : (
                  <div className={`flex flex-col items-center justify-center h-64 text-center px-6 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    <Users className="w-10 h-10 mb-2 opacity-50" />
                    <p className="text-sm">{bg ? "Изберете пациент от списъка" : "Pick a patient from the list"}</p>
                    <button
                      onClick={() => setMobileSection("patients")}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 bg-violet-100 text-violet-700"
                    >
                      {bg ? "Към списъка" : "To list"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* iter351.F: MD tab — patient-facing clinical modules
                (Клиничен анализ, Клинични фенотипи, Coverage phenotypes,
                Mind-Body, Епигенетична карта). Все patient-friendly UX/UI,
                видими САМО за clinicians (this entire page is clinician-gated). */}
            {mobileSection === "md" && (
              <div className="p-3 space-y-3" data-testid="doctor-mobile-section-md">
                {/* Header banner — MD context cue */}
                <div className={`rounded-2xl p-4 ${isDark ? "bg-gradient-to-br from-emerald-900/40 to-teal-900/30 border border-emerald-700/30" : "bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200"}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white text-lg shrink-0">
                      🩺
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                        {bg ? "MD — Клинични инструменти" : "MD — Clinical tools"}
                      </p>
                      <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {bg
                          ? "Дълбок патогенетичен анализ за клиницисти"
                          : "Deep pathogenic analysis for clinicians"}
                      </p>
                    </div>
                  </div>
                </div>

                <React.Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /></div>}>
                  {/* Клиничен анализ (gene-based AI insight) */}
                  <ClinicalAnalysis />

                  {/* Клинични фенотипи (MDR Class IIa — clinician-only) */}
                  <PhenotypeClassifier />

                  {/* Coverage phenotypes — Pathway Coverage за phenotype-driven nutrient gaps */}
                  <PathwayCoverageCard />

                  {/* Mind-Body Interventions */}
                  <MindBodyRecommender />

                  {/* Епигенетична карта */}
                  <EpigeneticMapPanel />
                </React.Suspense>
              </div>
            )}

            {/* iter397 — SOAP премахнат от mobile nav по заявка на потребителя. */}

            {mobileSection === "tools" && (
              <div className="p-3 space-y-3" data-testid="doctor-mobile-section-tools">
                <ToolItem id="alerts" label={bg ? "Известия" : "Alerts"} color="#8B5CF6">
                  <DoctorAlertsPanel onSelectPatient={(id) => { setSelectedId(id); setMobileSection("detail"); }} />
                </ToolItem>
                <ToolItem id="templates" label={bg ? "Шаблони" : "Templates"} color="#EC4899">
                  <ClinicalTemplatesPanel patientId={selectedId} />
                </ToolItem>
                <ToolItem id="subphenotype" label={bg ? "OGTT — Метаболитен subphenotype" : "OGTT subphenotype"} color="#06B6D4">
                  <MetabolicSubphenotypePanel />
                </ToolItem>
                <ToolItem id="nephro" label={bg ? "Нефро калкулатор" : "Nephrology calculator"} color="#0EA5E9">
                  <NephroCalculatorPanel />
                </ToolItem>
                <ToolItem id="compliance" label={bg ? "GDPR / EU AI Act одит" : "GDPR / EU AI Act audit"} color="#10B981">
                  <DoctorComplianceAuditPanel />
                </ToolItem>
                <ToolItem id="shared" label={bg ? "Споделени пациенти" : "Shared patients"} color="#7C3AED">
                  <SharedPatientsWidget />
                </ToolItem>
              </div>
            )}
          </div>

          {/* Mobile bottom navigation (<lg only) */}
          <DoctorMobileBottomNav
            active={mobileSection}
            onChange={setMobileSection}
          />

          {wizardOpen && (
            <CreatePatientWizard
              open={wizardOpen}
              onClose={() => setWizardOpen(false)}
              onCreated={handleCreated}
            />
          )}
        </div>
      </DoctorShell>
    </>
  );
}
